// Mietkonto eines Objekts (README §6): eine Quelle für Mietkonto der Einheit, Mahnwesen,
// Bankabgleich, Mieteingang, Finanzen, Status der Einheitenliste und Objekt-Saldo.

import { istWohnend } from '../dashboard/leerstand';
import { monatsAnfang, monatsLabel, plusMonate, runde } from './datum';
import { gesamtmieteAm, sollFuerHausgeld, sollFuerMietverhaeltnis, type HausgeldRoh, type MietverhaeltnisRoh } from './soll';
import { verrechne } from './verrechnung';
import { eigentuemerKonto, mieterKonto, type Buchung, type LedgerEntry, type Mietkonto, type MonatsZeile, type SollPosten } from './typen';

export interface EinheitKurz { id: string; bezeichnung: string | null }

export interface MietkontoEingabe {
  verwaltungsart: string | null;
  einheiten: readonly EinheitKurz[];
  /** MV/SEV: alle Mietverhältnisse des Objekts (aktuelle und ehemalige) inkl. Namen */
  mieter: readonly (MietverhaeltnisRoh & { vorname: string | null; nachname: string | null; firma_name: string | null })[];
  /** WEG: Eigentümer je Einheit mit Hausgeld */
  hausgeld: readonly (HausgeldRoh & { name: string; eigentuemer_id: string | null })[];
  buchungen: readonly Buchung[];
  /** Erster Monat, ab dem Soll gerechnet wird (app_settings.mietkonto_start) */
  start: string;
  /** heute (YYYY-MM-DD) */
  heute: string;
}

/** Offener Betrag ab dem ein Fall als Rückstand gilt (README: offen > 0.5). */
export const RUECKSTAND_SCHWELLE = 0.5;

const name = (m: { vorname: string | null; nachname: string | null; firma_name: string | null }): string =>
  (m.firma_name ?? '').trim() || [m.vorname, m.nachname].map(x => (x ?? '').trim()).filter(Boolean).join(' ') || 'Ohne Namen';

/** Zeigt die letzten 12 Monate (bis einschließlich des laufenden) aus den verrechneten Posten. */
function zwoelfMonate(posten: readonly (SollPosten & { bezahlt: number })[], heute: string): MonatsZeile[] {
  const aktuell = monatsAnfang(heute);
  return Array.from({ length: 12 }, (_, i) => plusMonate(aktuell, i - 11)).map(monat => {
    const imMonat = posten.filter(p => p.monat === monat);
    return {
      monat,
      label: monatsLabel(monat),
      soll: runde(imMonat.reduce((s, p) => s + p.betrag, 0)),
      ist: runde(imMonat.reduce((s, p) => s + p.bezahlt, 0)),
    };
  });
}

export function baueMietkonto(e: MietkontoEingabe): Mietkonto {
  const isWeg = (e.verwaltungsart ?? '').toUpperCase() === 'WEG';
  const bis = monatsAnfang(e.heute);
  const start = monatsAnfang(e.start);
  const bez = new Map(e.einheiten.map(x => [x.id, (x.bezeichnung ?? '').trim() || 'Ohne Bezeichnung']));
  const list: LedgerEntry[] = [];

  const konto = (k: { key: string; einheitId: string; personId: string | null; aktiv: boolean; payer: string; monthly: number; soll: SollPosten[] }) => {
    const buchungen = e.buchungen.filter(b => b.kontoSchluessel === k.key);
    // Mietverhältnisse ohne Soll und ohne Zahlungen (z. B. Mitmieter ohne eigene Miete) haben kein Konto.
    if (k.soll.length === 0 && buchungen.length === 0) return;
    const v = verrechne(k.soll, buchungen);
    const saldo = runde(v.istSumme - v.sollSumme);
    const imMonat = v.posten.filter(p => p.monat === bis);
    list.push({
      key: k.key,
      einheitId: k.einheitId,
      personId: k.personId,
      aktiv: k.aktiv,
      bez: bez.get(k.einheitId) ?? 'Ohne Bezeichnung',
      payer: k.payer,
      monthly: k.monthly,
      months: zwoelfMonate(v.posten, e.heute),
      pays: buchungen.filter(b => !b.storniert).sort((a, b) => b.datum.localeCompare(a.datum)).map(b => ({ id: b.id, d: b.datum, b: b.betrag, src: b.quelle })),
      sollSum: v.sollSumme,
      istSum: v.istSumme,
      saldo,
      offen: runde(Math.max(0, -saldo)),
      oktOpen: runde(imMonat.reduce((s, p) => s + Math.max(0, p.betrag - p.bezahlt), 0)),
      offenSeit: saldo < -RUECKSTAND_SCHWELLE ? v.offenSeit : null,
    });
  };

  if (isWeg) {
    for (const h of e.hausgeld) {
      if (!bez.has(h.einheit_id)) continue;
      const aktiv = !h.vertrag_bis || h.vertrag_bis >= e.heute;
      konto({
        key: eigentuemerKonto(h.eigentuemer_id ?? 'unbekannt', h.einheit_id),
        einheitId: h.einheit_id,
        personId: h.eigentuemer_id ?? null,
        aktiv,
        payer: h.name,
        monthly: aktiv ? runde(sollFuerHausgeld(h, bis, bis)[0]?.betrag ?? 0) : 0,
        soll: sollFuerHausgeld(h, start, bis),
      });
    }
  } else {
    for (const m of e.mieter) {
      if (!m.einheit_id || !bez.has(m.einheit_id)) continue;
      const aktiv = istWohnend(m.status);
      konto({
        key: mieterKonto(m.id),
        einheitId: m.einheit_id,
        personId: m.id,
        aktiv,
        payer: name(m),
        monthly: aktiv ? runde(gesamtmieteAm(m, e.heute)) : 0,
        soll: sollFuerMietverhaeltnis(m, start, bis),
      });
    }
  }

  list.sort((a, b) => a.bez.localeCompare(b.bez, 'de', { numeric: true }) || Number(b.aktiv) - Number(a.aktiv) || a.payer.localeCompare(b.payer, 'de'));
  const map: Record<string, LedgerEntry> = {};
  for (const l of list) map[l.key] = l;
  return { isWeg, list, map, offen: runde(list.reduce((s, l) => s + l.offen, 0)) };
}

/** Konten einer Einheit: aktuelle zuerst, dann ehemalige. */
export function kontenDerEinheit(k: Pick<Mietkonto, 'list'>, einheitId: string): LedgerEntry[] {
  return k.list.filter(l => l.einheitId === einheitId);
}

/** Konto(en) der aktuellen Mieter bzw. Eigentümer einer Einheit. */
export function aktuelleKonten(k: Pick<Mietkonto, 'list'>, einheitId: string): LedgerEntry[] {
  return kontenDerEinheit(k, einheitId).filter(l => l.aktiv);
}

/** Ehemalige mit offenem Rückstand (bleibt beim alten Mieter, Entscheidung 12.10.2026). */
export function ehemaligeMitRueckstand(k: Pick<Mietkonto, 'list'>): LedgerEntry[] {
  return k.list.filter(l => !l.aktiv && l.offen > RUECKSTAND_SCHWELLE);
}

/** Status der Einheit in der Einheitenliste: Rückstand der aktuellen Mieter (rot = offen > 0.5). */
export function einheitHatRueckstand(k: Pick<Mietkonto, 'list'>, einheitId: string): boolean {
  return aktuelleKonten(k, einheitId).some(l => l.offen > RUECKSTAND_SCHWELLE);
}

/** Objekt-Saldo für Liste und Portfolio: −Σ offen (README §6). */
export function objektSaldo(k: Pick<Mietkonto, 'offen'>): number {
  return runde(-k.offen);
}

/** Hat die Einheit einen Rückstand? (Status in der Einheitenliste, rot = offen > 0.5) */
export function istRueckstand(l: Pick<LedgerEntry, 'offen'> | undefined): boolean {
  return !!l && l.offen > RUECKSTAND_SCHWELLE;
}

export interface Mieteingang {
  soll: number;
  /** Σ min(ist, soll) je Einheit im Monat */
  ist: number;
  offen: number;
  /** 0–100 */
  quote: number;
  bezahlt: number;
  ausstehend: number;
}

/** Mieteingang eines Monats über alle Konten (README §6: Ist = Σ min(ist, soll)). */
export function mieteingang(k: Pick<Mietkonto, 'list'>, monat: string): Mieteingang {
  let soll = 0;
  let ist = 0;
  let bezahlt = 0;
  let ausstehend = 0;
  for (const l of k.list) {
    const z = l.months.find(m => m.monat === monatsAnfang(monat));
    if (!z || z.soll <= 0) continue;
    soll += z.soll;
    ist += Math.min(z.ist, z.soll);
    if (z.ist >= z.soll - RUECKSTAND_SCHWELLE) bezahlt += 1; else ausstehend += 1;
  }
  return {
    soll: runde(soll), ist: runde(ist), offen: runde(soll - ist),
    quote: soll > 0 ? Math.round((ist / soll) * 100) : 0, bezahlt, ausstehend,
  };
}

export interface MieterKontenEingabe {
  einheiten: readonly (EinheitKurz & { objekt_id: string })[];
  mieter: MietkontoEingabe['mieter'];
  buchungen: readonly Buchung[];
  start: string;
  heute: string;
}

/**
 * Mietkonten aller Mieter (Mieterliste): je Objekt dieselbe Rechnung wie baueMietkonto,
 * Ergebnis je Mieter-ID. Eine Ladung für alle Objekte – keine Abfrage je Objekt.
 */
export function mieterKonten(e: MieterKontenEingabe): Map<string, LedgerEntry> {
  const objektDerEinheit = new Map(e.einheiten.map(x => [x.id, x.objekt_id]));
  const gruppen = new Map<string, { einheiten: EinheitKurz[]; mieter: MietkontoEingabe['mieter'][number][]; buchungen: Buchung[] }>();
  const gruppe = (objektId: string) => {
    let g = gruppen.get(objektId);
    if (!g) { g = { einheiten: [], mieter: [], buchungen: [] }; gruppen.set(objektId, g); }
    return g;
  };
  for (const x of e.einheiten) gruppe(x.objekt_id).einheiten.push(x);
  for (const m of e.mieter) {
    const o = m.einheit_id ? objektDerEinheit.get(m.einheit_id) : undefined;
    if (o) gruppe(o).mieter.push(m);
  }
  for (const b of e.buchungen) {
    const o = objektDerEinheit.get(b.einheitId);
    if (o) gruppe(o).buchungen.push(b);
  }
  const ergebnis = new Map<string, LedgerEntry>();
  for (const g of gruppen.values()) {
    if (g.mieter.length === 0) continue;
    const k = baueMietkonto({ verwaltungsart: null, einheiten: g.einheiten, mieter: g.mieter, hausgeld: [], buchungen: g.buchungen, start: e.start, heute: e.heute });
    for (const l of k.list) if (l.personId) ergebnis.set(l.personId, l);
  }
  return ergebnis;
}
